class CookieBanner extends HTMLElement {
  constructor() {
    super();
    this.cookiePreferences = {
      required: true,
      personalization: false,
      marketing: false,
      analytics: false
    };
    this.init();
    console.log('CookieBanner initialized');
  }

  init() {
    // Wait for Customer Privacy API to load first
    const checkBanner = () => {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        // IMPORTANT: Get consent from Shopify API first (source of truth)
        const shopifyConsent = window.Shopify.customerPrivacy.currentVisitorConsent();
        
        // Sync from Shopify API to local state
        if (shopifyConsent) {
          this.cookiePreferences = {
            required: true,
            personalization: shopifyConsent.preferences === 'yes',
            marketing: shopifyConsent.marketing === 'yes',
            analytics: shopifyConsent.analytics === 'yes'
          };
          
          // Also restore to ConsentManager (Google Consent Mode)
          if (window.ConsentManager) {
            window.ConsentManager.updateConsent({
              analytics: this.cookiePreferences.analytics,
              marketing: this.cookiePreferences.marketing,
              preferences: this.cookiePreferences.personalization
            });
          }
        } else {
          // Fallback: Load from custom cookie if Shopify API has no consent yet
          const stored = this.getCookie('cookie_preferences');
          if (stored) {
            try {
              this.cookiePreferences = JSON.parse(stored);
              // Sync to Shopify API if we have stored preferences
              this.savePreferences();
            } catch (e) {
              console.warn('Could not parse stored preferences:', e);
            }
          }
        }
        
        // Check if banner should show
        const shouldShow = window.Shopify.customerPrivacy.shouldShowBanner();
        if (shouldShow) {
          // Banner will show - user needs to make a choice
          this.showBanner();
        } else {
          // Banner won't show - user already has consent (or no consent needed)
          // Don't block anything - respect existing consent
          // Only sync consent state to ConsentManager if we have preferences
          if (this.cookiePreferences) {
            if (window.ConsentManager) {
              window.ConsentManager.updateConsent({
                analytics: true,
                marketing: true,
                preferences: true
              });
            }
          }
        }
      } else {
        // Retry after 100ms if API not loaded yet
        setTimeout(checkBanner, 100);
      }
    };

    // Start checking after a short delay
    setTimeout(checkBanner, 500);
  }

  // Watch for TikTok loading after page load
  watchForTikTok() {
    if (this._tiktokWatcher) return; // Already watching
    
    let checkCount = 0;
    const maxChecks = 50; // Check for 5 seconds (50 * 100ms)
    
    this._tiktokWatcher = setInterval(() => {
      checkCount++;
      
      // If TikTok appears and we don't have marketing consent, block it
      if (typeof window.ttq !== 'undefined' && (!this.cookiePreferences.marketing || !this.cookiePreferences.analytics)) {
        console.log('TikTok detected after page load - blocking now');
        this.blockTrackingFunctions();
      }
      
      // Stop watching after max checks or if TikTok is blocked
      if (checkCount >= maxChecks || (typeof window.ttq !== 'undefined' && window.ttq._blocked)) {
        clearInterval(this._tiktokWatcher);
        this._tiktokWatcher = null;
      }
    }, 100);
  }

  showBanner() {
    const banner = this.querySelector('.cookie-banner');
    if (banner) {
      banner.classList.add('show');
    }
  }

  hideBanner() {
    const banner = this.querySelector('.cookie-banner');
    if (banner) {
      banner.classList.remove('show');
      banner.classList.add('cookie-banner--hidden');
    }
  }

  showModal() {
    const modal = this.querySelector('.cookie-modal');
    if (modal) {
      modal.classList.add('show');
      document.body.style.overflow = 'hidden';
    }
  }

  hideModal() {
    const modal = this.querySelector('.cookie-modal');
    if (modal) {
      modal.classList.remove('show');
      document.body.style.overflow = '';
    }
  }

  acceptAll() {
    this.cookiePreferences = {
      required: true,
      personalization: true,
      marketing: true,
      analytics: true
    };
    this.savePreferences();
    this.hideBanner();
  }

  declineAll() {
    this.cookiePreferences = {
      required: true,
      personalization: false,
      marketing: false,
      analytics: false
    };
    // Clear marketing cookies immediately when declining
    this.clearMarketingCookies();
    this.savePreferences();
    this.hideBanner();
    this.hideModal();
    
    // Only block tracking if banner was shown (user actively declined)
    // Block tracking functions if they exist
    this.blockTrackingFunctions();
    
    // Start watching for TikTok to load later
    this.watchForTikTok();
  }

  confirmChoices() {
    this.savePreferences();
    this.hideBanner();
    this.hideModal();
  }

  // Withdraw consent - reset all preferences to denied
  withdrawConsent() {
    this.cookiePreferences = {
      required: true,
      personalization: false,
      marketing: false,
      analytics: false
    };
    this.savePreferences();
    this.hideBanner();
    this.hideModal();
    
    // Clear marketing cookies if possible
    this.clearMarketingCookies();
    
    // Block tracking functions when user withdraws consent
    this.blockTrackingFunctions();
    
    // Start watching for TikTok to load later
    this.watchForTikTok();
  }

  // Clear marketing cookies (helper function)
  clearMarketingCookies() {
    const marketingCookies = ['_ga', '_gid', '_gat', '_fbp', '_fbc', '_gcl_au', '_gcl_aw', '_gcl_dc', '_gcl_gf', '_gcl_gb'];
    const domain = window.location.hostname;
    const domainParts = domain.split('.');
    
    // Try to clear cookies for current domain and parent domains
    const domainsToTry = [
      domain,
      '.' + domain,
      '.' + domainParts.slice(-2).join('.'), // .example.com
      domainParts.slice(-2).join('.') // example.com (without dot)
    ];
    
    // Also try common cookie domains
    if (domain.includes('.')) {
      domainsToTry.push('.' + domainParts.slice(-2).join('.'));
    }
    
    marketingCookies.forEach(cookieName => {
      // Try different domain combinations
      domainsToTry.forEach(dom => {
        // Clear with domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${dom}`;
        // Clear without domain (current path)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
        // Clear with root path
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${dom}; SameSite=None; Secure`;
      });
      
      // Also try to clear with empty value
      document.cookie = `${cookieName}=; max-age=0; path=/`;
      document.cookie = `${cookieName}=; max-age=0; path=/; domain=${domain}`;
      if (domain.includes('.')) {
        document.cookie = `${cookieName}=; max-age=0; path=/; domain=.${domain}`;
      }
    });
    
    // Clear localStorage tracking data
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('_ga') || key.startsWith('_gid') || key.startsWith('_fbp') || key.includes('analytics') || key.includes('tracking'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.warn('Could not clear localStorage:', e);
    }
    
    console.log('Marketing cookies cleared (attempted)');
  }

  // Block tracking functions if they were already loaded
  blockTrackingFunctions() {
    // Block Facebook Pixel
    if (typeof fbq !== 'undefined') {
      try {
        // Revoke consent
        if (typeof fbq === 'function') {
          fbq('consent', 'revoke');
        }
        // Override to prevent tracking
        window.fbq = function() {
          console.log('Facebook Pixel blocked - no consent');
        };
      } catch (e) {
        console.warn('Could not block Facebook Pixel:', e);
      }
    }
    
    // Block TikTok Pixel - More aggressive blocking
    try {
      // If ttq exists, completely override it
      if (typeof window.ttq !== 'undefined') {
        // Store original if not already stored
        if (!window._ttqOriginal) {
          window._ttqOriginal = window.ttq;
        }
        
        // Create a completely blocked version
        const blockedTtq = function() {
          console.log('TikTok Pixel blocked - no consent');
          return blockedTtq;
        };
        
        // Copy array methods but make them no-ops
        blockedTtq.push = function() {
          console.log('TikTok Pixel blocked - no consent');
          return blockedTtq;
        };
        
        blockedTtq.load = function() {
          console.log('TikTok Pixel load blocked - no consent');
          return blockedTtq;
        };
        
        blockedTtq.page = function() {
          console.log('TikTok Pixel page blocked - no consent');
          return blockedTtq;
        };
        
        blockedTtq.track = function() {
          console.log('TikTok Pixel track blocked - no consent');
          return blockedTtq;
        };
        
        blockedTtq.identify = function() {
          console.log('TikTok Pixel identify blocked - no consent');
          return blockedTtq;
        };
        
        // Mark as blocked
        blockedTtq._blocked = true;
        
        // Replace the entire object
        window.ttq = blockedTtq;
        
        // Also try to prevent new ttq from being set
        Object.defineProperty(window, 'ttq', {
          value: blockedTtq,
          writable: false,
          configurable: false
        });
      } else {
        // Pre-emptively block ttq before it loads
        Object.defineProperty(window, 'ttq', {
          value: (function() {
            const blocked = function() {
              console.log('TikTok Pixel blocked - no consent');
              return blocked;
            };
            blocked.push = blocked.load = blocked.page = blocked.track = blocked.identify = blocked;
            blocked._blocked = true;
            return blocked;
          })(),
          writable: false,
          configurable: false
        });
      }
    } catch (e) {
      console.warn('Could not block TikTok Pixel:', e);
      // Fallback: try to override push if ttq exists
      if (typeof window.ttq !== 'undefined' && window.ttq.push) {
        try {
          window.ttq.push = function() {
            console.log('TikTok Pixel blocked - no consent (fallback)');
            return window.ttq;
          };
          window.ttq._blocked = true;
        } catch (e2) {
          console.warn('Fallback TikTok block also failed:', e2);
        }
      }
    }
    
    // Block Klaviyo
    if (typeof _learnq !== 'undefined') {
      try {
        window._learnq = window._learnq || [];
        const originalPush = window._learnq.push;
        window._learnq.push = function() {
          console.log('Klaviyo blocked - no consent');
        };
      } catch (e) {
        console.warn('Could not block Klaviyo:', e);
      }
    }
    
    // Google Analytics is handled by Consent Mode, but ensure it's denied
    if (window.dataLayer && typeof gtag !== 'undefined') {
      try {
        gtag('consent', 'update', {
          'ad_storage': 'denied',
          'analytics_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      } catch (e) {
        console.warn('Could not update Google Consent Mode:', e);
      }
    }
    
    console.log('Tracking functions blocked');
  }

  savePreferences() {
    const preferences = JSON.stringify(this.cookiePreferences);
    this.setCookie('cookie_preferences', preferences, 365);
    
    // Update Shopify Customer Privacy API
    if (window.Shopify && window.Shopify.customerPrivacy) {
      window.Shopify.customerPrivacy.setTrackingConsent(
        {
          "analytics":    this.cookiePreferences.analytics,
          "marketing":    this.cookiePreferences.marketing,
          "preferences":  this.cookiePreferences.personalization
        },
        () => console.log("Shopify consent captured")
      );
    }

    // Update Consent Manager (Google Consent Mode)
    if (window.ConsentManager) {
      window.ConsentManager.updateConsent({
        analytics: this.cookiePreferences.analytics,
        marketing: this.cookiePreferences.marketing,
        preferences: this.cookiePreferences.personalization
      });
    }

    // Trigger custom events for other scripts
    const cookieEvent = new CustomEvent('cookiePreferencesUpdated', {
      detail: this.cookiePreferences
    });
    document.dispatchEvent(cookieEvent);
  }

  toggleCategory(category) {
    if (category === 'required') {
      return; 
    }
    
    this.cookiePreferences[category] = !this.cookiePreferences[category];
    this.updateCheckboxUI(category);
  }

  updateCheckboxUI(category) {
    const checkbox = this.querySelector(`[data-category="${category}"]`);
    if (checkbox) {
      if (this.cookiePreferences[category]) {
        checkbox.classList.add('checked');
      } else {
        checkbox.classList.remove('checked');
      }
    }
  }

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  connectedCallback() {
    // Add event listeners
    this.addEventListener('click', (e) => {
      const target = e.target;

      if (target.classList.contains('cookie-banner__btn--manage')) {
        e.preventDefault();
        this.showModal();
      }

      if (target.classList.contains('cookie-banner__btn--accept')) {
        e.preventDefault();
        this.acceptAll();
      }

      if (target.classList.contains('cookie-modal__close') || target.closest('.cookie-modal__close')) {
        e.preventDefault();
        this.hideModal();
      }

      if (target.classList.contains('cookie-modal__btn--confirm')) {
        e.preventDefault();
        this.confirmChoices();
      }

      if (target.classList.contains('cookie-modal__btn--decline')) {
        e.preventDefault();
        this.declineAll();
      }

      if (target.classList.contains('cookie-modal__btn--accept')) {
        e.preventDefault();
        this.acceptAll();
        this.hideModal();
      }

      if (target.classList.contains('cookie-modal__btn--withdraw')) {
        e.preventDefault();
        this.withdrawConsent();
      }

      // Category checkboxes
      if (target.classList.contains('cookie-category__checkbox')) {
        e.preventDefault();
        const category = target.getAttribute('data-category');
        this.toggleCategory(category);
      }

      if (target.classList.contains('cookie-banner__link')) {
        e.preventDefault();
        window.open('/policies/privacy-policy', '_blank');
      }
    });

    // Close modal when clicking outside
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('cookie-modal')) {
        this.hideModal();
      }
    });

    // Initialize checkbox states
    this.querySelectorAll('.cookie-category__checkbox').forEach(checkbox => {
      const category = checkbox.getAttribute('data-category');
      this.updateCheckboxUI(category);
    });
  }
}

// Register the custom element
customElements.define('cookie-banner', CookieBanner);

// Initialize cookie banner - works even if DOMContentLoaded already fired
function initCookieBanner() {
  // Check if already initialized
  if (document.querySelector('cookie-banner')) {
    console.log('Cookie banner already initialized');
    return;
  }
  
  // Create and append the cookie banner element
  const cookieBannerElement = document.createElement('cookie-banner');
  cookieBannerElement.innerHTML = `
    <!-- Cookie Banner -->
    <div class="cookie-banner">
      <div class="cookie-banner__content">
        <p class="cookie-banner__text">
          We use cookies to improve your experience. You can opt out of certain cookies. 
          Find out more in our<a href="#" class="cookie-banner__link"> Privacy policy.</a>
        </p>
      </div>
      <div class="cookie-banner__buttons">
        <button class="cookie-banner__btn cookie-banner__btn--manage">Manage</button>
        <button class="cookie-banner__btn cookie-banner__btn--accept">Accept all</button>
      </div>
    </div>

    <!-- Cookie Modal -->
    <div class="cookie-modal">
      <div class="cookie-modal__content">
        <div class="cookie-modal__header">
          <h2 class="cookie-modal__title">Cookie and privacy preferences</h2>
          <h3 class="cookie-modal__subtitle">You control your data</h3>
          <p class="cookie-modal__description">
            Learn more about the cookies we use, and choose which cookies to allow.
          </p>
          <button class="cookie-modal__close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 6L18 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M18 6L6 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div class="cookie-modal__body">
          <div class="cookie-category">
            <div class="cookie-category__header">
              <h4 class="cookie-category__title">Required</h4>
              <div class="cookie-category__checkbox required" data-category="required"></div>
            </div>
            <p class="cookie-category__description">
              These cookies are necessary for the site to function properly, including capabilities like logging in and adding items to the cart.
            </p>
          </div>
          
          <div class="cookie-category">
            <div class="cookie-category__header">
              <h4 class="cookie-category__title">Personalization</h4>
              <div class="cookie-category__checkbox" data-category="personalization"></div>
            </div>
            <p class="cookie-category__description">
              These cookies store details about your actions to personalize your next visit to the website.
            </p>
          </div>
          
          <div class="cookie-category">
            <div class="cookie-category__header">
              <h4 class="cookie-category__title">Marketing</h4>
              <div class="cookie-category__checkbox" data-category="marketing"></div>
            </div>
            <p class="cookie-category__description">
              These cookies are used by us and our partners, including Shopify, to optimize marketing communications and show you ads on other websites.
            </p>
          </div>
          
          <div class="cookie-category">
            <div class="cookie-category__header">
              <h4 class="cookie-category__title">Analytics</h4>
              <div class="cookie-category__checkbox" data-category="analytics"></div>
            </div>
            <p class="cookie-category__description">
              These cookies help us understand how you interact with the site. We use this data to identify areas to improve.
            </p>
          </div>
        </div>
        
        <div class="cookie-modal__footer">
          <button class="cookie-modal__btn cookie-modal__btn--confirm">Confirm Choices</button>
          <button class="cookie-modal__btn cookie-modal__btn--decline">Decline All</button>
          <button class="cookie-modal__btn cookie-modal__btn--accept">Accept all</button>
          <button class="cookie-modal__btn cookie-modal__btn--withdraw" style="margin-top: 12px; background: transparent; color: #666; border: none; text-decoration: underline; font-size: 14px;">Withdraw Consent</button>
        </div>
      </div>
    </div>
  `;
  
  document.body.appendChild(cookieBannerElement);
  console.log('Cookie banner element added to DOM');
}

// Initialize immediately if DOM is ready, otherwise wait for DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCookieBanner);
} else {
  // DOM already loaded, initialize immediately
  initCookieBanner();
}

// Helper function to check TikTok Beacon blocking status
window.checkTikTokBlocking = function() {
  const results = {
    ttqExists: typeof window.ttq !== 'undefined',
    isBlocked: false,
    isOriginalFunction: false,
    networkRequests: [],
    details: {},
    blockingMethod: 'unknown'
  };

  if (results.ttqExists) {
    // Check if ttq itself is a function (blocked version)
    const ttqType = typeof window.ttq;
    const ttqString = window.ttq.toString();
    
    // Check multiple indicators of blocking
    const hasBlockedMessage = ttqString.includes('blocked') || ttqString.includes('no consent');
    const hasBlockedFlag = window.ttq._blocked === true;
    const isFunctionNotArray = ttqType === 'function' && !Array.isArray(window.ttq);
    const hasOriginalStored = typeof window._ttqOriginal !== 'undefined';
    
    // More comprehensive check
    if (window.ttq.push) {
      const pushFunction = window.ttq.push.toString();
      const pushIsBlocked = pushFunction.includes('blocked') || pushFunction.includes('no consent');
      
      results.isBlocked = hasBlockedMessage || hasBlockedFlag || (isFunctionNotArray && pushIsBlocked);
      results.blockingMethod = hasBlockedFlag ? 'flag' : (pushIsBlocked ? 'push_override' : (isFunctionNotArray ? 'function_override' : 'none'));
    } else {
      results.isBlocked = hasBlockedMessage || hasBlockedFlag || isFunctionNotArray;
      results.blockingMethod = hasBlockedFlag ? 'flag' : (isFunctionNotArray ? 'function_override' : 'none');
    }
    
    results.isOriginalFunction = !results.isBlocked && !hasOriginalStored && Array.isArray(window.ttq);
    
    results.details = {
      ttqType: ttqType,
      isArray: Array.isArray(window.ttq),
      ttqString: ttqString.substring(0, 150) + '...',
      pushFunction: window.ttq.push ? window.ttq.push.toString().substring(0, 150) + '...' : 'N/A',
      hasOriginalStored: hasOriginalStored,
      blockedFlag: window.ttq._blocked || false,
      queueLength: Array.isArray(window.ttq) ? window.ttq.length : 'N/A'
    };
  } else {
    // ttq doesn't exist - check if it's pre-emptively blocked
    try {
      const descriptor = Object.getOwnPropertyDescriptor(window, 'ttq');
      if (descriptor && !descriptor.writable) {
        results.isBlocked = true;
        results.blockingMethod = 'preemptive_property_descriptor';
        results.details = {
          message: 'ttq is pre-emptively blocked via property descriptor',
          writable: descriptor.writable,
          configurable: descriptor.configurable
        };
      }
    } catch (e) {
      // Can't check descriptor
    }
  }

  // Check for TikTok network requests
  if (window.performance && window.performance.getEntriesByType) {
    const resources = window.performance.getEntriesByType('resource');
    const tiktokRequests = resources.filter(r => 
      r.name.includes('tiktok.com') || 
      r.name.includes('tiktokads') || 
      r.name.includes('analytics.tiktok') ||
      r.name.includes('analytics.tiktok.com')
    );
    results.networkRequests = tiktokRequests.map(r => ({
      url: r.name,
      type: r.initiatorType,
      duration: r.duration,
      timestamp: r.startTime
    }));
  }

  return results;
};

// Console helper for easy testing
console.log('%cTikTok Blocking Checker', 'color: #ff0050; font-weight: bold; font-size: 14px;');
console.log('Run: checkTikTokBlocking() to see TikTok blocking status');

window.CookieBanner = CookieBanner; 